import { union } from 'es-toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import {
  formatLumberGrade,
  formatLumberSpecies,
  Lumber,
  LumberGrade,
  LumberPropertySet,
  LumberSpecies,
  LumberTreatmentType,
} from '@paragon/contract-library';

export function getLumberPropertySetKey(
  lumber: Pick<
    Lumber,
    'species' | 'tangibleSpecies' | 'grade' | 'tangibleGrades' | 'treatmentType' | 'nominalThickness' | 'nominalWidth'
  >,
): string {
  return `${formatLumberSpecies(lumber.species, lumber.tangibleSpecies)}-${formatLumberGrade(lumber.grade, lumber.tangibleGrades)}-${lumber.treatmentType}-${lumber.nominalThickness}-${lumber.nominalWidth}`;
}

export interface LumberPropertySetKeyed extends LumberPropertySet {
  readonly species: LumberSpecies;
  readonly tangibleSpecies: readonly LumberSpecies[];
  readonly grade: LumberGrade;
  readonly tangibleGrades: readonly LumberGrade[];
  readonly treatmentType: LumberTreatmentType;
  readonly nominalThickness: string;
  readonly nominalWidth: string;
}

interface LumberPropertySetStore {
  readonly lumberPropertySets: readonly LumberPropertySetKeyed[];

  // Tracks lumber property sets that are known to not exist, so we don't request them multiple times.
  readonly notFoundSets: readonly string[];

  readonly requests: { readonly [key: string]: string };
}

const initialState: Readonly<LumberPropertySetStore> = {
  lumberPropertySets: [],
  notFoundSets: [],
  requests: {},
};

export function shouldMakeLumberPropertySetRequest(state: LumberPropertySetStore, lumberPropertySetKey: string) {
  return !(state.requests[lumberPropertySetKey] || state.notFoundSets.includes(lumberPropertySetKey));
}

const lumberPropertySetsSlice = createSlice({
  name: 'lumberPropertySets',
  initialState,
  reducers: {
    setLumberPropertySetRequested: (state, action: PayloadAction<Lumber>) => {
      const lumberPropertySetKey = getLumberPropertySetKey(action.payload);

      if (!shouldMakeLumberPropertySetRequest(state, lumberPropertySetKey)) {
        throw new Error('Requesting a lumber property set that has already been requested or does not exist.');
      }

      state.requests[lumberPropertySetKey] = lumberPropertySetKey;
    },
    setLumberPropertySetForLumber: (
      state,
      action: PayloadAction<
        Lumber & {
          lumberPropertySet: LumberPropertySet;
        }
      >,
    ) => {
      const {
        species,
        tangibleSpecies,
        grade,
        tangibleGrades,
        treatmentType,
        nominalWidth,
        nominalThickness,
        lumberPropertySet,
      } = action.payload;

      const lumberPropertySetWithIdentifyingInformation: LumberPropertySetKeyed = {
        species,
        tangibleSpecies,
        grade,
        tangibleGrades,
        treatmentType,
        nominalThickness,
        nominalWidth,
        ...lumberPropertySet,
      };

      state.lumberPropertySets.push(castDraft(lumberPropertySetWithIdentifyingInformation));
    },
    setLumberPropertySetFailed: (state, action: PayloadAction<Lumber>) => {
      const lumberPropertySetKey = getLumberPropertySetKey(action.payload);
      state.notFoundSets = union(state.notFoundSets, [lumberPropertySetKey]);
      delete state.requests[lumberPropertySetKey];
    },
  },
});

export const { setLumberPropertySetRequested, setLumberPropertySetForLumber, setLumberPropertySetFailed } =
  lumberPropertySetsSlice.actions;
export const lumberPropertySets = lumberPropertySetsSlice.reducer;
